import type {
  HandbookPageFill,
  HandbookSetupAnswers,
  HandbookSetupGoalId,
  HandbookSetupTopicId,
} from './handbookContract';

export interface SetupTopic {
  id: HandbookSetupTopicId;
  label: string;
  question: string;
  placeholder: string;
}

/** The core policy topics, in the order the guided setup asks about them. */
export const SETUP_TOPICS: SetupTopic[] = [
  {
    id: 'leave',
    label: 'Leave and holiday',
    question: 'How do you handle holiday and other leave?',
    placeholder:
      'e.g. 30 days’ holiday plus bank holidays. Book in BreatheHR with two weeks’ notice. Six months’ full pay for parental leave.',
  },
  {
    id: 'sickness',
    label: 'Sickness and absence',
    question: 'What happens when someone is off sick?',
    placeholder:
      'e.g. Full pay for the first 10 sick days a year. Message your manager before 9am. A fit note is needed after 5 days.',
  },
  {
    id: 'conduct',
    label: 'Code of conduct',
    question: 'How do you expect people to work and behave?',
    placeholder:
      'e.g. In the office on Tuesdays and Thursdays. No gifts from clients worth more than £50. Smart casual dress.',
  },
  {
    id: 'grievance',
    label: 'Grievance and disciplinary',
    question: 'How do people raise a problem, and how do you handle it?',
    placeholder:
      'e.g. Raise grievances with Jo Smith (People lead) rather than your manager. We reply within 5 working days.',
  },
  {
    id: 'expenses',
    label: 'Expenses and company spending',
    question: 'How do people claim expenses and spend company money?',
    placeholder:
      'e.g. Claim in Xero within 30 days. Up to £25 a day for meals when travelling. Anything over £500 needs Sam’s approval.',
  },
];

export const TOPIC_LABELS = Object.fromEntries(
  SETUP_TOPICS.map((t) => [t.id, t.label]),
) as Record<HandbookSetupTopicId, string>;

export const POLICIES_SECTION_TITLE = 'Policies';

export type SetupGoalId = HandbookSetupGoalId;

/** What an admin can choose to cover; each choice adds its own steps. */
export const SETUP_GOALS: {
  id: SetupGoalId;
  label: string;
  hint: string;
  /** Estimated team time saved a week: questions a week × ~8 minutes each */
  minutesSaved: number;
}[] = [
  {
    id: 'policies',
    minutesSaved: 60,
    label: 'Company policies',
    hint: 'Leave, sickness, conduct, grievances and expenses',
  },
  {
    id: 'company',
    minutesSaved: 45,
    label: 'Information about my company and products',
    hint: 'What you do, what you sell and who you sell to',
  },
  {
    id: 'team',
    minutesSaved: 60,
    label: 'Team structure and who to contact',
    hint: 'Teams, who owns what and key contacts',
  },
  {
    id: 'ways',
    minutesSaved: 45,
    label: 'How we work',
    hint: 'Tools, processes and onboarding',
  },
  {
    id: 'own',
    minutesSaved: 30,
    label: 'I want to add my own information',
    hint: 'Create your own sections and pages',
  },
];

export function formatTimeSaved(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const parts = [
    hours ? `${hours} ${hours === 1 ? 'hour' : 'hours'}` : null,
    mins ? `${mins} minutes` : null,
  ];
  return parts.filter(Boolean).join(' ');
}

export type DetailGoalId = 'company' | 'team' | 'ways';

export interface DetailQuestion {
  id: string;
  label: string;
  /** Also the title of the page the answer becomes */
  short: string;
  placeholder: string;
}

/**
 * The free-text questions for each non-policy goal. Mirrors the backend's
 * setup questions: each goal becomes a section titled `title`, each question
 * a page titled `short`.
 */
export const DETAIL_STEPS: Record<
  DetailGoalId,
  { title: string; questions: DetailQuestion[] }
> = {
  company: {
    title: 'Your company and products',
    questions: [
      {
        id: 'company.about',
        short: 'What you do',
        label: 'What does your company do?',
        placeholder:
          'e.g. We’re a 20-person design studio in Manchester. We make brand identities and websites.',
      },
      {
        id: 'company.products',
        short: 'Products and services',
        label: 'What are your main products or services?',
        placeholder:
          'e.g. Brand identities from £8k, websites from £15k and monthly design retainers.',
      },
      {
        id: 'company.customers',
        short: 'Customers',
        label: 'Who are your customers?',
        placeholder:
          'e.g. Mostly UK startups and charities. Our biggest accounts are Northwind and Fabrikam.',
      },
    ],
  },
  team: {
    title: 'Your team',
    questions: [
      {
        id: 'team.structure',
        short: 'Team structure',
        label: 'How is the team organised?',
        placeholder:
          'e.g. Three teams: Design (led by Priya), Engineering (Tom) and Operations (Sam).',
      },
      {
        id: 'team.contacts',
        short: 'Who to ask',
        label: 'Who should people ask about what?',
        placeholder:
          'e.g. Payroll and contracts: Sam. Laptops and IT: Tom. Client escalations: Priya.',
      },
    ],
  },
  ways: {
    title: 'How you work',
    questions: [
      {
        id: 'ways.tools',
        short: 'Tools',
        label: 'Which tools do you use, and what for?',
        placeholder:
          'e.g. Slack for chat, Linear for projects, Xero for expenses and BreatheHR for holiday.',
      },
      {
        id: 'ways.processes',
        short: 'Processes',
        label: 'Which processes should people know about?',
        placeholder:
          'e.g. Book travel through TravelPerk. Raise IT problems in #it-help.',
      },
      {
        id: 'ways.onboarding',
        short: 'New starters',
        label: 'What should a new starter know in their first week?',
        placeholder:
          'e.g. Your laptop arrives on day one. You meet your buddy on Monday. Payday is the 25th.',
      },
    ],
  },
};

export const DETAIL_GOALS: DetailGoalId[] = ['company', 'team', 'ways'];

/** The document a fill wrote a page from, for "Written from …" labels. */
export function fillSourceTitle(fill: HandbookPageFill): string {
  const titles = Array.from(
    new Set(fill.sources.map((s) => s.title).filter(Boolean)),
  );
  return titles.length > 0 ? titles.join(', ') : 'your document';
}

export type SetupStep =
  | 'intro'
  | 'files'
  | HandbookSetupTopicId
  | DetailGoalId
  | 'own'
  | 'review'
  | 'generate';

/** The steps for the chosen goals, in order; `generate` follows `review`. */
export function setupSteps(goals: SetupGoalId[]): SetupStep[] {
  const steps: SetupStep[] = ['intro'];
  if (goals.some((g) => g !== 'own')) steps.push('files');
  if (goals.includes('policies')) steps.push(...SETUP_TOPICS.map((t) => t.id));
  DETAIL_GOALS.forEach((g) => {
    if (goals.includes(g)) steps.push(g);
  });
  if (goals.includes('own')) steps.push('own');
  steps.push('review');
  return steps;
}

const ALL_STEPS = setupSteps(SETUP_GOALS.map((g) => g.id));

export function isSetupStep(value: string | null): value is SetupStep {
  return (
    value !== null &&
    (ALL_STEPS.includes(value as SetupStep) || value === 'generate')
  );
}

export function emptyAnswers(): HandbookSetupAnswers {
  return { goals: [], topics: {}, details: {}, prefilled: {}, ownSections: [] };
}

export function topicAnswer(
  answers: HandbookSetupAnswers,
  id: HandbookSetupTopicId,
): string {
  return answers.topics[id] ?? '';
}

export type TopicState = 'added' | 'missing';

export function topicState(answer: string): TopicState {
  return answer.trim() ? 'added' : 'missing';
}

/**
 * Merges what a prefill found into the answers: only fields the admin has not
 * written anything in are taken, so a document never overwrites an answer.
 */
export function mergePrefill(
  answers: HandbookSetupAnswers,
  found: Pick<HandbookSetupAnswers, 'topics' | 'details' | 'prefilled'>,
): HandbookSetupAnswers {
  const topics = { ...answers.topics };
  const details = { ...answers.details };
  const prefilled = { ...answers.prefilled };
  (Object.keys(found.topics) as HandbookSetupTopicId[]).forEach((id) => {
    const text = found.topics[id];
    if (text && !topics[id]?.trim()) {
      topics[id] = text;
      if (found.prefilled[id]) prefilled[id] = found.prefilled[id];
    }
  });
  Object.entries(found.details).forEach(([id, text]) => {
    if (text && !details[id]?.trim()) {
      details[id] = text;
      if (found.prefilled[id]) prefilled[id] = found.prefilled[id];
    }
  });
  return { ...answers, topics, details, prefilled };
}

export interface BlankSection {
  area: string;
  items: string[];
}

/**
 * The pages the setup will leave (or left) blank, by section — the same
 * outline the backend builds from these answers, so the warning before
 * creating matches the blank drafts after.
 */
export function blankSections(answers: HandbookSetupAnswers): BlankSection[] {
  const sections: BlankSection[] = [];
  if (answers.goals.includes('policies')) {
    sections.push({
      area: POLICIES_SECTION_TITLE,
      items: SETUP_TOPICS.filter((t) => !answers.topics[t.id]?.trim()).map(
        (t) => t.label,
      ),
    });
  }
  DETAIL_GOALS.forEach((goal) => {
    if (!answers.goals.includes(goal)) return;
    const step = DETAIL_STEPS[goal];
    sections.push({
      area: step.title,
      items: step.questions
        .filter((q) => !answers.details[q.id]?.trim())
        .map((q) => q.short),
    });
  });
  if (answers.goals.includes('own')) {
    answers.ownSections.forEach((section) => {
      if (!section.title.trim()) return;
      sections.push({
        area: section.title.trim(),
        items: section.pages
          .filter((p) => p.title.trim() && !p.fill?.bodyMarkdown.trim())
          .map((p) => p.title.trim()),
      });
    });
  }
  return sections.filter((section) => section.items.length > 0);
}

export function blankSectionsMessage(
  answers: HandbookSetupAnswers,
): string | null {
  const count = blankSections(answers).reduce(
    (n, section) => n + section.items.length,
    0,
  );
  if (count === 0) return null;
  return `You've left ${count === 1 ? 'this page' : 'these pages'} blank. Your secretary will say it doesn't know the answer.`;
}

const SLUG_MAX_LENGTH = 120;

/** Mirrors the backend: "Holiday & carry-over (UK)" → "holiday-carry-over-uk" */
export function handbookSlug(title: string): string {
  const slug = title
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, SLUG_MAX_LENGTH)
    .replace(/-+$/g, '');
  return slug.length > 0 ? slug : 'page';
}

export interface TakenTitle {
  slug: string;
  title: string;
  /** Section the page sits in; null for a section itself */
  section: string | null;
}

/** Why a new page title cannot be used: it clashes with an existing page or a repeat in the list. */
export function titleConflict(
  title: string,
  others: string[],
  taken: TakenTitle[],
): string | null {
  const slug = handbookSlug(title);
  if (others.some((o) => handbookSlug(o) === slug)) {
    return `You’ve listed “${title}” twice.`;
  }
  const hit = taken.find((t) => t.slug === slug);
  if (!hit) return null;
  return hit.section
    ? `“${hit.title}” already exists under ${hit.section}. Pick a different title.`
    : `“${hit.title}” is already a section. Pick a different title.`;
}
